/**
 * AccountingDataTable — Ön Muhasebe Ortak Tablo Bileşeni
 *
 * Tüm ön muhasebe sayfalarında tutarlı tablo görünümü sağlar.
 * MUI Table üzerine inşa edilmiş, sıralama, sayfalama, loading ve boş durum desteği içerir.
 *
 * Kullanım:
 *   <AccountingDataTable
 *     columns={[
 *       { id: 'name', label: 'Ad', field: 'name', sortable: true },
 *       { id: 'price', label: 'Fiyat', align: 'right', render: (row) => formatCurrency(row.price) },
 *     ]}
 *     rows={data}
 *     getRowId={(row) => row.id}
 *     loading={loading}
 *     onRowClick={(row) => handleClick(row)}
 *   />
 */

import React, { Fragment, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  Collapse,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';

// ════════════════════════════════════════════════════════════════
// Sabit Stiller — tüm tablolarda aynı görünüm
// ════════════════════════════════════════════════════════════════

const STYLES = {
  paper: {
    borderRadius: 2,
    overflow: 'hidden',
  } as SxProps<Theme>,

  headerCell: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    bgcolor: 'primary.main',
    whiteSpace: 'nowrap',
    py: 1,
    px: 1.5,
    borderRight: '1px solid rgba(255,255,255,0.15)',
    '&:last-child': { borderRight: 'none' },
  } as SxProps<Theme>,

  dataRow: {
    height: '38px',
    '& td': { py: 0.4, px: 1.5 },
    '&:nth-of-type(even)': { backgroundColor: 'rgba(0, 0, 0, 0.015)' },
    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' },
    transition: 'background-color 0.15s ease',
  } as SxProps<Theme>,

  dataCell: {
    fontSize: '0.78rem',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as SxProps<Theme>,

  pagination: {
    borderTop: '1px solid rgba(0,0,0,0.08)',
    '& .MuiTablePagination-toolbar': {
      minHeight: '42px',
      height: '42px',
      padding: '0 12px',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      margin: 0,
      fontSize: '0.78rem',
    },
  } as SxProps<Theme>,

  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 10,
    borderRadius: 2,
  } as SxProps<Theme>,

  emptyBox: {
    py: 6,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
  } as SxProps<Theme>,

};

// ════════════════════════════════════════════════════════════════
// Tip Tanımları
// ════════════════════════════════════════════════════════════════

type SortDirection = 'asc' | 'desc';

export interface DataTableColumn<T> {
  /** Benzersiz kolon kimliği */
  id: string;
  /** Başlık metni */
  label: string;
  /** Genişlik (ör: '15%', '120px') */
  width?: string;
  /** Hizalama */
  align?: 'left' | 'center' | 'right';
  /** Sıralamaya izin ver */
  sortable?: boolean;
  /** Veri alanı — basit metin gösterimi + sıralama için */
  field?: keyof T;
  /** Özel render fonksiyonu — bu tanımlıysa field yerine bu kullanılır */
  render?: (row: T, index: number) => React.ReactNode;
  /** Tooltip göster (overflow içerikler için) */
  tooltip?: boolean | ((row: T) => string);
  /** Hücreye ek sx */
  cellSx?: SxProps<Theme>;
  /** Min genişlik */
  minWidth?: string;
}

export interface AccountingDataTableProps<T> {
  /** Kolon tanımları */
  columns: DataTableColumn<T>[];
  /** Satır verileri */
  rows: T[];
  /** Satır kimliği döndüren fonksiyon */
  getRowId: (row: T) => string;

  // ─── Loading & Hata ─────────────────────────────
  /** Yükleniyor durumu */
  loading?: boolean;

  // ─── Sayfalama ───────────────────────────────────
  /** Sayfalama aktif mi (varsayılan: true) */
  pagination?: boolean;
  /** Varsayılan sayfa başına satır (varsayılan: 25) */
  defaultRowsPerPage?: number;
  /** Sayfa başına satır seçenekleri */
  rowsPerPageOptions?: number[];

  // ─── Server-Side Pagination ──────────────────────
  /**
   * Server-side sayfalama aktif mi?
   * true ise dahili sayfalama (client-side slice) devre dışı kalır.
   * Veri zaten server'da sayfalanmış gelir.
   * totalCount, page, pageSize, onPageChange prop'ları zorunlu olur.
   */
  serverSidePagination?: boolean;
  /** Toplam kayıt sayısı (server-side pagination için) */
  totalCount?: number;
  /** Kontrollü sayfa numarası — 0-based (server-side pagination için) */
  page?: number;
  /** Kontrollü sayfa boyutu (server-side pagination için) */
  pageSize?: number;
  /** Sayfa veya sayfa boyutu değişim callback'i (server-side pagination için) */
  onPageChange?: (page: number, pageSize: number) => void;

  // ─── Sıralama ────────────────────────────────────
  /** Varsayılan sıralama alanı */
  defaultSortField?: string;
  /** Varsayılan sıralama yönü */
  defaultSortDirection?: SortDirection;

  // ─── Server-Side Sıralama ─────────────────────────
  /** Server-side sıralama callback — tanımlıysa dahili sıralama devre dışı kalır */
  onSort?: (field: string, direction: SortDirection) => void;
  /** Server-side kontrollü sıralama alanı */
  sortField?: string;
  /** Server-side kontrollü sıralama yönü */
  sortDirection?: SortDirection;

  // ─── Satır Etkileşimi ────────────────────────────
  /** Satır tıklama */
  onRowClick?: (row: T) => void;
  /** Satır üzerine tıklanabilir mi (cursor: pointer) */
  clickable?: boolean;
  /** Satıra özel sx (koşullu arka plan renkleri vb.) */
  getRowSx?: (row: T) => SxProps<Theme>;
  /** Satır seçim uygunluğu (checkbox enable/disable) */
  isRowSelectable?: (row: T) => boolean;

  // ─── Hiyerarşik Yapı ─────────────────────────────
  /**
   * Hiyerarşik (parent-child) tablolarda çocuk satırların parent'ını belirler.
   * Tanımlıysa sıralama yalnızca parent satırları sıralar; çocuklar her zaman
   * kendi parent'larının altında kalır.
   * Parent satırlar için null/undefined döndürülmelidir.
   */
  getParentId?: (row: T) => string | null | undefined;

  // ─── Boş Durum ───────────────────────────────────
  /** Boş durum mesajı */
  emptyMessage?: string;
  /** Boş durum ikonu */
  emptyIcon?: React.ReactNode;
  // ─── Seçim (Checkbox) ──────────────────────────
  /** Checkbox seçim modu aktif mi */
  selectable?: boolean;
  /** Seçili satır ID'leri */
  selectedIds?: string[];
  /** Tek satır seçim/kaldırma */
  onSelectId?: (id: string) => void;
  /** Tümünü seç/kaldır */
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  // ─── Genişletilebilir Satırlar ─────────────────
  /** Genişletilmiş satır ID'leri */
  expandedRowIds?: Set<string>;
  /** Satır genişlet/daralt toggle */
  onToggleExpand?: (id: string) => void;
  /** Genişletilmiş içerik render fonksiyonu */
  renderExpandedContent?: (row: T) => React.ReactNode;
  /** Satırın genişletilebilir olup olmadığını belirler (ör: sadece alt kayıtları olan satırlar) */
  isExpandable?: (row: T) => boolean;  /** Otomatik expand kolonu gösterilsin mi (varsayılan: true). false ise expand butonu kolonlara manuel eklenir ama Collapse satırı yine çalışır */
  showExpandColumn?: boolean;

  // ─── Tablo Ayarları ──────────────────────────────
  /** Tablo düzeni: fixed veya auto (varsayılan: fixed) */
  tableLayout?: 'fixed' | 'auto';
  /** Paper elevation (varsayılan: 2) */
  elevation?: number;
  /** Dış sarmalayıcıya ek sx */
  sx?: SxProps<Theme>;
  /** Tablo aria-label */
  ariaLabel?: string;
  /** Maksimum yükseklik (scroll için) */
  maxHeight?: number | string;

  // ─── Sütun Boyutlandırma ──────────────────────────
  /** localStorage anahtarı — verildiğinde sütun genişlikleri sürüklenebilir ve localStorage'da saklanır */
  columnResizeStorageKey?: string;
}

// ════════════════════════════════════════════════════════════════
// Yardımcı fonksiyonlar
// ════════════════════════════════════════════════════════════════

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

function descendingComparator<T>(a: T, b: T, orderBy: string): number {
  const valA = getNestedValue(a, orderBy);
  const valB = getNestedValue(b, orderBy);

  if (valB == null && valA == null) return 0;
  if (valB == null) return -1;
  if (valA == null) return 1;

  if (typeof valA === 'string' && typeof valB === 'string') {
    return valB.localeCompare(valA, 'tr');
  }
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator<T>(order: SortDirection, orderBy: string): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// ════════════════════════════════════════════════════════════════
// Bileşen
// ════════════════════════════════════════════════════════════════

function AccountingDataTable<T>(props: AccountingDataTableProps<T>) {
  const { t } = useTranslation('accounting');
  const {
    columns,
    rows,
    getRowId,
    loading = false,
    pagination = true,
    defaultRowsPerPage = 25,
    rowsPerPageOptions = [25, 50, 100],
    // Server-side pagination
    serverSidePagination = false,
    totalCount: externalTotalCount,
    page: externalPage,
    pageSize: externalPageSize,
    onPageChange,
    // Sıralama
    defaultSortField,
    defaultSortDirection = 'desc',
    onSort,
    sortField: externalSortField,
    sortDirection: externalSortDirection,
    onRowClick,
    clickable,
    getRowSx,
    isRowSelectable = () => true,
    getParentId,
    emptyMessage = t('dataTable.emptyMessage', { defaultValue: 'Kayıt bulunamadı.' }),
    emptyIcon,
    tableLayout = 'fixed',
    elevation = 2,
    sx,
    ariaLabel,
    maxHeight,
    // Seçim (Checkbox)
    selectable = false,
    selectedIds = [],
    onSelectId,
    onSelectAll,
    // Genişletilebilir Satırlar
    expandedRowIds,
    onToggleExpand,
    renderExpandedContent,
    isExpandable,
    showExpandColumn = true,
    // Sütun Boyutlandırma
    columnResizeStorageKey,
  } = props;

  // Otomatik expand kolonu gösterilecek mi
  const showExpCol = !!renderExpandedContent && showExpandColumn;

  // Toplam kolon sayısı (colSpan hesabı için)
  const totalColSpan = columns.length
    + (showExpCol ? 1 : 0)
    + (selectable ? 1 : 0);

  // ─── State ─────────────────────────────────────────
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState<string>(defaultSortField ?? '');
  const [order, setOrder] = useState<SortDirection>(defaultSortDirection);

  // ─── Sütun Boyutlandırma State ─────────────────────
  // Versiyon: eski ölçüm yöntemiyle kaydedilen geniş değerleri sıfırlamak için
  const COL_WIDTHS_VERSION = 2;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (!columnResizeStorageKey) return {};
    try {
      const storageKey = `table-col-widths-${columnResizeStorageKey}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Versiyon kontrolü — eski format (versiyon yok veya farklı) ise sıfırla
        if (parsed && typeof parsed === 'object') {
          if (parsed.__version === COL_WIDTHS_VERSION && Object.keys(parsed).length > 1) {
            const { __version, ...widths } = parsed;
            return widths;
          }
          // Eski versiyon veya versiyon yok — sil, yeniden ölçülecek
          localStorage.removeItem(storageKey);
        }
      }
    } catch { /* sessizce devam */ }
    return {};
  });
  const resizeRef = useRef<{
    colId: string;
    startX: number;
    startWidth: number;
    nextColId: string | null;
    nextStartWidth: number;
  } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const headerCellRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const widthsRef = useRef<Record<string, number>>(columnWidths);
  widthsRef.current = columnWidths;

  // Tüm sütunların piksel genişliklerini ölç — eksik olan varsa hepsini set et
  const ensureAllColumnWidths = useCallback((): Record<string, number> => {
    const current = { ...widthsRef.current };
    let anyMissing = false;
    columns.forEach(col => {
      if (!current[col.id]) {
        const cell = headerCellRefs.current[col.id];
        if (cell) {
          // Hücrenin minimum genişliğini hesapla: scrollWidth (içerik genişliği)
          const contentWidth = cell.scrollWidth;
          // İçerik genişliği + biraz pay (resize handle için)
          const measuredWidth = Math.max(contentWidth, 60);
          current[col.id] = Math.round(measuredWidth);
          anyMissing = true;
        }
      }
    });
    if (anyMissing) {
      widthsRef.current = current;
      setColumnWidths(current);
      if (columnResizeStorageKey) {
        try {
          localStorage.setItem(`table-col-widths-${columnResizeStorageKey}`, JSON.stringify({ ...current, __version: COL_WIDTHS_VERSION }));
        } catch { /* sessizce devam */ }
      }
    }
    return current;
  }, [columns, columnResizeStorageKey]);

  // İlk renderdan sonra tüm sütun genişliklerini ölç (localStorage boşsa)
  // Ayrıca kolon ID'leri değişmişse eski genişlikleri sıfırla
  useEffect(() => {
    if (!columnResizeStorageKey) return;

    // Kolon ID'leri uyuşmazsa eski veriyi sıfırla (kolon ekleme/kaldırma sonrası)
    const currentKeys = Object.keys(widthsRef.current);
    const columnIds = columns.map(c => c.id);
    if (currentKeys.length > 0) {
      const hasObsolete = currentKeys.some(k => !columnIds.includes(k));
      const hasMissing = columnIds.some(id => !currentKeys.includes(id));
      if (hasObsolete || hasMissing) {
        // Uyumsuzluk — sıfırla, yeniden ölçülecek
        widthsRef.current = {};
        setColumnWidths({});
        try { localStorage.removeItem(`table-col-widths-${columnResizeStorageKey}`); } catch { /* */ }
      }
    }

    if (Object.keys(widthsRef.current).length >= columns.length) return;
    const raf = requestAnimationFrame(() => {
      ensureAllColumnWidths();
    });
    return () => cancelAnimationFrame(raf);
  }, [columnResizeStorageKey, columns.length, ensureAllColumnWidths]);

  // Resize başlat (mousedown) — önce TÜM sütunları sabitle, sonra sürüklemeyi başlat
  const handleResizeStart = useCallback((e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    // ÖNCELİKLE tüm sütunların piksel genişliklerini sabitle
    const allWidths = ensureAllColumnWidths();

    const startWidth = allWidths[colId];
    if (!startWidth) return;

    // Sağındaki sütunu bul; son sütunsa solundakini kullan (kompanse için)
    const colIndex = columns.findIndex(c => c.id === colId);
    let nextColId: string | null = null;
    let nextStartWidth = 0;
    if (colIndex >= 0 && colIndex < columns.length - 1) {
      nextColId = columns[colIndex + 1].id;
      nextStartWidth = allWidths[nextColId] || 0;
    } else if (colIndex === columns.length - 1 && colIndex > 0) {
      // Son sütun — önceki sütunla kompanse et
      nextColId = columns[colIndex - 1].id;
      nextStartWidth = allWidths[nextColId] || 0;
    }

    resizeRef.current = { colId, startX: e.clientX, startWidth, nextColId, nextStartWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columns, ensureAllColumnWidths]);

  // Resize sırasında (mousemove) ve bitişinde (mouseup)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { colId, startX, startWidth, nextColId, nextStartWidth } = resizeRef.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(40, startWidth + diff);

      const updated = { ...widthsRef.current, [colId]: newWidth };

      // Yanındaki sütun varsa ters yönde boyutlandır
      if (nextColId && nextStartWidth > 0) {
        const nextNew = Math.max(40, nextStartWidth - diff);
        updated[nextColId] = nextNew;
      }

      widthsRef.current = updated;
      setColumnWidths(updated);
    };

    const handleMouseUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // localStorage'a kaydet
      if (columnResizeStorageKey) {
        try {
          localStorage.setItem(
            `table-col-widths-${columnResizeStorageKey}`,
            JSON.stringify({ ...widthsRef.current, __version: COL_WIDTHS_VERSION })
          );
        } catch { /* sessizce devam */ }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [columnResizeStorageKey]);

  // Server-side ise harici state kullan, değilse dahili
  const page = serverSidePagination ? (externalPage ?? 0) : internalPage;
  const rowsPerPage = serverSidePagination ? (externalPageSize ?? defaultRowsPerPage) : internalRowsPerPage;
  const setPage = serverSidePagination
    ? (p: number) => onPageChange?.(p, rowsPerPage)
    : setInternalPage;

  // ─── Sıralama Handler ──────────────────────────────
  const handleRequestSort = useCallback((columnId: string) => {
    if (onSort) {
      // Server-side sıralama: callback'e delege et
      const col = columns.find(c => c.id === columnId);
      const sortKey = col?.field ? String(col.field) : columnId;
      const currentDir = externalSortDirection ?? 'desc';
      const newDir = (externalSortField === sortKey)
        ? (currentDir === 'asc' ? 'desc' : 'asc')
        : 'desc';
      onSort(sortKey, newDir);
    } else {
      // Client-side sıralama
      const isAsc = orderBy === columnId && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(columnId);
    }
  }, [onSort, externalSortField, externalSortDirection, order, orderBy, columns]);

  // ─── Sayfalama Handler'ları ────────────────────────
  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    if (serverSidePagination) {
      onPageChange?.(newPage, rowsPerPage);
    } else {
      setInternalPage(newPage);
    }
  }, [serverSidePagination, onPageChange, rowsPerPage]);

  const handleChangeRowsPerPage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (serverSidePagination) {
      onPageChange?.(0, newSize);
    } else {
      setInternalRowsPerPage(newSize);
      setInternalPage(0);
    }
  }, [serverSidePagination, onPageChange]);

  // ─── Sıralanmış + Sayfalanmış Veri ────────────────
  const sortedRows = useMemo(() => {
    // Server-side sıralama: veri zaten sıralı gelir, tekrar sıralama yapma
    if (onSort) return rows;
    if (!orderBy) return rows;
    // Sıralama yapılacak alan: column.field veya column.id
    const col = columns.find(c => c.id === orderBy);
    const sortField = col?.field ? String(col.field) : orderBy;
    const comparator = getComparator<T>(order, sortField);

    // Hiyerarşik tablo: yalnızca parent'ları sırala, çocuklar parent'ın altında kalır
    if (getParentId) {
      // Parent satırları ayır
      const parents = rows.filter(r => !getParentId(r));
      // Çocukları parent ID'ye göre grupla (orijinal sıra korunur)
      const childrenMap = new Map<string, T[]>();
      rows.forEach(r => {
        const pid = getParentId(r);
        if (pid) {
          if (!childrenMap.has(pid)) childrenMap.set(pid, []);
          childrenMap.get(pid)!.push(r);
        }
      });
      // Parent'ları sırala, ardından her parent'ın çocuklarını ekle
      const sortedParents = [...parents].sort(comparator);
      const result: T[] = [];
      sortedParents.forEach(parent => {
        result.push(parent);
        const parentId = getRowId(parent);
        const children = childrenMap.get(parentId);
        if (children) result.push(...children);
      });
      return result;
    }

    // Düz tablo: tüm satırları sırala
    return [...rows].sort(comparator);
  }, [rows, orderBy, order, columns, getParentId, getRowId, onSort]);

  const displayedRows = useMemo(() => {
    // Server-side pagination: veri zaten sayfalanmış gelir, slice yapma
    if (serverSidePagination) return sortedRows;
    if (!pagination) return sortedRows;
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, pagination, serverSidePagination, page, rowsPerPage]);

  const selectableRowIds = useMemo(() => {
    if (!selectable) return [] as string[];
    return displayedRows.filter((row) => isRowSelectable(row)).map((row) => getRowId(row));
  }, [displayedRows, selectable, isRowSelectable, getRowId]);

  const selectedSelectableCount = useMemo(
    () => selectableRowIds.filter((id) => selectedIds.includes(id)).length,
    [selectableRowIds, selectedIds]
  );

  const allSelectableChecked = selectableRowIds.length > 0 && selectedSelectableCount === selectableRowIds.length;
  const someSelectableChecked = selectedSelectableCount > 0 && selectedSelectableCount < selectableRowIds.length;

  // Sayfa geçersiz kaldıysa sıfırla (sadece client-side pagination için)
  React.useEffect(() => {
    if (serverSidePagination) return;
    const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    if (page > maxPage) setInternalPage(maxPage);
  }, [rows.length, rowsPerPage, page, serverSidePagination]);

  // ─── Hücre Değeri ──────────────────────────────────
  const getCellContent = useCallback(
    (col: DataTableColumn<T>, row: T, index: number): React.ReactNode => {
      if (col.render) return col.render(row, index);
      if (col.field) {
        const val = (row as any)[col.field];
        return val != null ? String(val) : '-';
      }
      return '-';
    },
    [],
  );

  // ─── İmleç stili ──────────────────────────────────
  const isClickable = clickable ?? !!onRowClick;

  return (
    <Box sx={{ position: 'relative', ...((sx as any) || {}) }}>
      {/* Loading Overlay */}
      {loading && (
        <Box sx={STYLES.loadingOverlay}>
          <CircularProgress size={36} />
        </Box>
      )}

      <Paper elevation={elevation} sx={STYLES.paper}>
        <TableContainer sx={{ ...(maxHeight ? { maxHeight } : {}), overflowX: 'auto' }}>
          <Table
            ref={tableRef}
            size="small"
            stickyHeader
            sx={{
              tableLayout: columnResizeStorageKey
                ? (Object.keys(columnWidths).length > 0 ? 'fixed' : 'auto')
                : tableLayout,
              width: '100%',
            }}
            aria-label={ariaLabel}
          >
            {/* ─── Header ─── */}
            <TableHead>
              <TableRow>
                {/* Expand kolonu başlığı */}
                {showExpCol && (
                  <TableCell
                    sx={{
                      ...(STYLES.headerCell as any),
                      width: '50px',
                      minWidth: '50px',
                      maxWidth: '50px',
                      padding: '4px',
                    }}
                  />
                )}
                {/* Checkbox kolonu başlığı */}
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      ...(STYLES.headerCell as any),
                      width: '50px',
                    }}
                  >
                    {onSelectAll && (
                      <Checkbox
                        indeterminate={someSelectableChecked}
                        checked={allSelectableChecked}
                        onChange={onSelectAll}
                        disabled={selectableRowIds.length === 0}
                        sx={{
                          color: 'white',
                          '&.Mui-checked': { color: 'white' },
                          '&.MuiCheckbox-indeterminate': { color: 'white' },
                        }}
                      />
                    )}
                  </TableCell>
                )}
                {columns.map((col) => {
                  const resizable = !!columnResizeStorageKey;
                  const dynWidth = columnWidths[col.id];
                  return (
                    <TableCell
                      key={col.id}
                      ref={(el: HTMLTableCellElement | null) => { headerCellRefs.current[col.id] = el; }}
                      align={col.align ?? 'left'}
                      sx={{
                        ...(STYLES.headerCell as any),
                        width: dynWidth ? `${dynWidth}px` : col.width,
                        minWidth: dynWidth ? `${dynWidth}px` : col.minWidth,
                        ...(resizable ? { position: 'relative' } : {}),
                      }}
                      sortDirection={orderBy === col.id ? order : false}
                    >
                      {col.sortable ? (
                        <Box
                          onClick={() => handleRequestSort(col.id)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.85 },
                          }}
                        >
                          {col.label}
                          {(() => {
                            const sortKey = col.field ? String(col.field) : col.id;
                            const isActive = onSort ? externalSortField === sortKey : orderBy === col.id;
                            const dir = onSort ? (externalSortDirection ?? 'desc') : order;
                            if (isActive && dir === 'asc') return <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5, color: 'rgba(255,255,255,0.9)' }} />;
                            if (isActive && dir === 'desc') return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, color: 'rgba(255,255,255,0.9)' }} />;
                            return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, color: 'rgba(255,255,255,0.3)' }} />;
                          })()}
                        </Box>
                      ) : (
                        col.label
                      )}
                      {/* Resize Handle */}
                      {resizable && (
                        <Box
                          onMouseDown={(e) => handleResizeStart(e, col.id)}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '6px',
                            cursor: 'col-resize',
                            zIndex: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.3)',
                            },
                          }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            {/* ─── Body ─── */}
            <TableBody>
              {displayedRows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={totalColSpan} align="center">
                    <Box sx={STYLES.emptyBox}>
                      {emptyIcon ?? <InboxIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                      <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                displayedRows.map((row, rowIndex) => {
                  const rowId = getRowId(row);
                  const isExpanded = expandedRowIds?.has(rowId) ?? false;
                  const isSelected = selectable && selectedIds.includes(rowId);
                  const rowSelectable = isRowSelectable(row);

                  return (
                    <Fragment key={rowId}>
                      <TableRow
                        hover
                        sx={{
                          ...(STYLES.dataRow as any),
                          cursor: isClickable ? 'pointer' : 'default',
                          ...(isSelected ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {}),
                          ...(getRowSx ? (getRowSx(row) as any) : {}),
                        }}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {/* Expand butonu */}
                        {showExpCol && (
                          <TableCell
                            sx={{
                              ...(STYLES.dataCell as any),
                              width: '50px',
                              minWidth: '50px',
                              maxWidth: '50px',
                              padding: '0 4px',
                            }}
                            align="center"
                          >
                            {onToggleExpand && (!isExpandable || isExpandable(row)) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleExpand(rowId);
                                }}
                              >
                                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                        {/* Checkbox */}
                        {selectable && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => onSelectId?.(rowId)}
                              disabled={!rowSelectable}
                            />
                          </TableCell>
                        )}
                        {columns.map((col) => {
                        const content = getCellContent(col, row, rowIndex);

                        // Tooltip desteği
                        const tooltipText =
                          col.tooltip === true && typeof content === 'string'
                            ? content
                            : typeof col.tooltip === 'function'
                              ? col.tooltip(row)
                              : undefined;

                        const cell = (
                          <TableCell
                            key={col.id}
                            align={col.align ?? 'left'}
                            sx={{
                              ...(STYLES.dataCell as any),
                              ...(col.cellSx as any),
                            }}
                          >
                            {content}
                          </TableCell>
                        );

                        return tooltipText ? (
                          <Tooltip key={col.id} title={tooltipText} arrow placement="top">
                            {cell}
                          </Tooltip>
                        ) : (
                          cell
                        );
                      })}
                      </TableRow>

                      {/* Genişletilmiş içerik satırı */}
                      {renderExpandedContent && isExpanded && (!isExpandable || isExpandable(row)) && (
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={totalColSpan}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              {renderExpandedContent(row)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ─── Sayfalama ─── */}
        {(pagination || serverSidePagination) && (
          <TablePagination
            component="div"
            count={serverSidePagination ? (externalTotalCount ?? 0) : rows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage={t('dataTable.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              t('dataTable.displayedRows', { count, from, to })
            }
            sx={STYLES.pagination}
          />
        )}
      </Paper>
    </Box>
  );
}

export default AccountingDataTable;

// Stilleri dışa aç — özel ihtiyaçlar için
export { STYLES as DATATABLE_STYLES };
